import { Milestone, SummerCyoOffer, SummerOfferResponse } from '@/models/leaderboard';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

@Component({
  name: 'CmgLeaderboardCyoRewardModal',
})
export default class CmgLeaderboardCyoRewardModal extends Vue {  
  errorState = false;
  isLoading = false;
  offerResponse: SummerOfferResponse | null = null;
  selectedOffer: SummerCyoOffer | null = null;

  @Watch('visible')
  async handleVisibleChange(newValue: boolean) {
    if (newValue && !this.comingFromTermsAndConditions) {
      this.selectedOffer = null;
      await this.loadOffers();
    }
  }

  get comingFromTermsAndConditions(): boolean {
    return this.$appStore.state.ui.modals.summerLeaderboardCyoReward.args?.onBack || false;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.summerLeaderboardCyoReward.visible;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get milestone(): Milestone | undefined {
    return this.$appStore.state.ui.modals.summerLeaderboardCyoReward.args?.milestone;
  }

  get milestoneUnlockedText(): string {
    const milestone = this.milestone;
    if (!milestone) return '';
    return `${milestone.title} UNLOCKED`;
  }

  get title(): string {
    const summerOffers = this.offerResponse;
    if (!summerOffers) return '';
    return `${summerOffers.title}`;
  }

  get offerEndDate(): string {
    const summerOffers = this.offerResponse;
    if (!summerOffers) return '';
    const raw = summerOffers.endDate;
    const endDate = raw ? (typeof raw === 'string' ? new Date(raw) : raw) : null;
    const dateString = endDate ? endDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
    return dateString ? `Select by ${dateString}` : '';
  }

  get offers(): SummerCyoOffer[] {
    return this.offerResponse?.offers || [];
  }

  async loadOffers() {
    try {
      this.isLoading = true;
      if (this.milestone?.month !== undefined) {
        this.offerResponse = await this.$services.leaderboard.getSummerOffers(this.milestone.month);
      }
    } catch (error) {
      this.$services.ui.setModalVisibility('tryAgainModal', true, { onClickTryAgain: this.tryAgain });
    } finally {
      this.isLoading = false;
    }
  }

  async tryAgain(){
    await this.loadOffers();
  }

  selectOffer(offer: SummerCyoOffer) {
      this.selectedOffer = offer;
      this.errorState = false;
  }

  onClickTermsAndConditions(event: any) {
    const milestone = this.milestone;
    const onBack = () => {
        this.$services.ui.setModalVisibility('summerLeaderboardCyoReward', true, { milestone, onBack: true });
        this.$services.ui.setModalVisibility('rewardTermsAndConditions', false);
    };

    const offer = this.offers.find(offer => offer.id === event.currentTarget.id);
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('rewardTermsAndConditions', true, { termsAndConditions: offer?.terms, onBack });
    this.$services.ui.firstFocus();
    this.$services.ui.setModalVisibility('summerLeaderboardCyoReward', false);
  } 

  async onClickChooseOffer(selectedOffer: SummerCyoOffer) {
    if (!selectedOffer) {
      this.errorState = true;
      return;
    }

    this.isLoading = true;
    const offer = selectedOffer;

    await this.$services.leaderboard.redeemSummerOffer({
        redemptionType: offer.offerType,
        offerId: offer.id.toString(),
        month: this.milestone?.month ?? 0,
        year: new Date().getFullYear(),
        segment: this.milestone?.segment ?? 0,
    })
    .then(async purchasedReward => {
        if (purchasedReward !== null) {
          this.$services.ui.setModalVisibility('summerLeaderboardCyoRedemptionSuccessModal', true, {
              offer: offer,
          });

          // After redeeming the offer, we want to immediately remove the CYO eligibility from the milestone to 
          // signal to other components that this milestone is no longer eligible.  Quicker than calling api again.
          this.milestone!.eligibleForChooseYourOwnReward = false;
          this.milestone!.eligibleForChooseYourOwnRewardExpirationDate = undefined;

          this.onClose();

          await this.$services.customer.refreshCustomerProfileInfo();
        }})
        .catch(async error => {
            this.$services.ui.setModalVisibility('tryAgainModal', true, { onClickTryAgain: () => this.onClickChooseOffer(selectedOffer) });
        })
        .finally(() => {
            this.$services.ui.setAppPepperVisibility(false);
            this.isLoading = false;
            this.onClose();
        });
  }

  onClose() {
    this.$services.ui.setModalVisibility('summerLeaderboardCyoReward', false);
  }
}
